/**
 * Zod schemas for V4 API response envelopes.
 *
 * Imports item-level schemas from view-item.schema.ts and adds
 * the pipeline and response wrapper types.
 */

import { z } from 'zod';
import type { ViewContext } from './registries';
import {
  ViewItemSchema,
  ViewBundleSchema,
  ViewGroupSchema,
  ViewControlsSchema,
} from './view-item.schema';
import type { ViewItem, ViewBundle, ViewGroup, ViewControls } from './view-item.schema';

// Pipeline types

/** Single stage entry from the pipeline_stage_config table */
export const PipelineStageConfigSchema = z.object({
  apiName: z.string(),      // exact StageName picklist value (join key against resolved.opportunityStage)
  label: z.string(),        // display label (e.g. "1. Engage")
  sortOrder: z.number(),    // picklist position, determines pipeline column order
  isClosed: z.boolean(),   // closed stage (Closed Won / Closed Lost)
  isWon: z.boolean(),      // win stage (only meaningful when isClosed=true)
});
export type PipelineStageConfig = z.infer<typeof PipelineStageConfigSchema>;

export const PipelineOpportunitySchema = z.object({
  id: z.string().nullable(),
  name: z.string().nullable(),
  stage: z.string().nullable(),
  stageNumber: z.number().nullable(),
  amount: z.number().nullable(),
  closeDate: z.string().nullable(),
  potentialContractValue: z.number().nullable(),
});
export type PipelineOpportunity = z.infer<typeof PipelineOpportunitySchema>;

export const PipelineAccountSchema = z.object({
  accountId: z.string(),
  accountName: z.string(),
  accountFitScore: z.number().nullable(),
  accountPropensityScore: z.number().nullable(),
  opportunity: PipelineOpportunitySchema.nullable(),
  unreadCount: z.number(),
  pendingApprovals: z.number(),
  totalCount: z.number(),
});
export type PipelineAccount = z.infer<typeof PipelineAccountSchema>;

export const PipelineStageSchema = z.object({
  key: z.string(),
  label: z.string(),
  number: z.number(),
  totalValue: z.number(),
  accountCount: z.number(),
  accounts: z.array(PipelineAccountSchema),
});
export type PipelineStage = z.infer<typeof PipelineStageSchema>;

export const PipelineSummarySchema = z.object({
  stages: z.array(PipelineStageSchema),
});
export type PipelineSummary = z.infer<typeof PipelineSummarySchema>;

// ViewArtifactsResponse — main API response envelope

export const ViewArtifactsResponseSchema = z.object({
  items: z.array(ViewItemSchema),
  bundles: z.array(ViewBundleSchema),
  groups: z.array(ViewGroupSchema),
  controls: ViewControlsSchema.optional(),
  pipeline: PipelineSummarySchema.optional(),
  meta: z.object({
    total: z.number(),
    limit: z.number(),
    viewContext: z.string(), // ViewContext — registry-derived
    etag: z.string().optional(),
    nextCursor: z.string().nullable().optional(),
    prevCursor: z.string().nullable().optional(),
    artifactCards: z.array(z.string()).optional(),
  }),
});

export type ViewArtifactsResponse = Omit<z.infer<typeof ViewArtifactsResponseSchema>, 'items' | 'bundles' | 'groups' | 'controls' | 'meta'> & {
  items: ViewItem[];
  bundles: ViewBundle[];
  groups: ViewGroup[];
  controls?: ViewControls;
  meta: Omit<z.infer<typeof ViewArtifactsResponseSchema>['meta'], 'viewContext'> & {
    /** Narrowed from z.string() to registry union */
    viewContext: ViewContext;
  };
};
